import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Dropdown } from "primereact/dropdown";
import { CustomButton } from "../../components/CustomButton";
import {
  API_ADD_SUBSCRIPTION,
  API_GET_SUBSCRIPTION,
  API_UPGRADE_SUBSCRIPTION,
} from "../../api/company.service";

const subscriptionOptions = [
  { label: "Trial", value: 10, id: 1 },
  { label: "Basic", value: 1, id: 2 },
  { label: "Standard", value: 2, id: 3 },
  { label: "Pro", value: 3, id: 4 },
];

const planValueByName = {
  trial: 10,
  basic: 1,
  standard: 2,
  growth: 1,
  pro: 3,
};

const resolvePlanValue = (subscription) => {
  const type = subscription?.subscriptiontype;
  if (!type) return null;
  if (type.id !== undefined && type.id !== null) {
    const optionById = subscriptionOptions.find((option) => option.id === type.id);
    if (optionById) return optionById.value;
  }
  return planValueByName[String(type.name || "").toLowerCase()] ?? null;
};

const Subscription = () => {
  const { companyId } = useParams();
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchSubscription = useCallback(async () => {
    try {
      const data = await API_GET_SUBSCRIPTION(companyId);
      const subscription = data.subscription || null;
      setSubscriptionData(subscription);
      setSelectedPlan(resolvePlanValue(subscription));
    } catch (error) {
      console.error("Error fetching subscription details:", error);
    }
  }, [companyId]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  const subscriptionStats = useMemo(() => {
    return [
      {
        label: "Current Plan",
        value: subscriptionData?.subscriptiontype?.name || "No active subscription",
      },
      {
        label: "Payment Type",
        value: subscriptionData?.paymentType || "-",
      },
      {
        label: "Mode",
        value: selectedPlan === 10 ? "Prepaid" : "Postpaid",
      },
    ];
  }, [selectedPlan, subscriptionData]);

  const handleUpgrade = async () => {
    if (!selectedPlan) return;

    try {
      const payload = {
        companyId,
        subscriptiontypeId: selectedPlan,
        paymentType: selectedPlan === 10 ? "prepaid" : "postpaid",
      };

      setLoading(true);
      if (subscriptionData) {
        await API_UPGRADE_SUBSCRIPTION(payload);
      } else {
        await API_ADD_SUBSCRIPTION(payload);
      }
      await fetchSubscription();
    } catch (error) {
      console.error("Error updating subscription:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="company-subscription-layout">
      <div className="company-subscription-stats">
        {subscriptionStats.map((item) => (
          <div className="company-insight-card" key={item.label}>
            <span>{item.label}</span>
            <strong>{item.value}</strong>
          </div>
        ))}
      </div>

      <div className="surface-panel">
        <div className="surface-panel-header">
          <div>
            <h2 className="surface-panel-title">Subscription management</h2>
          </div>
        </div>

        <div className="company-dialog-grid">
          <div className="company-dialog-field">
            <label htmlFor="plan">Select plan</label>
            <Dropdown
              inputId="plan"
              value={selectedPlan}
              options={subscriptionOptions}
              onChange={(event) => setSelectedPlan(event.value)}
              placeholder="Choose a plan"
            />
          </div>
        </div>

        <div className="company-dialog-actions">
          <CustomButton
            label={subscriptionData ? "Upgrade Plan" : "Activate Plan"}
            icon="pi pi-sync"
            onClick={handleUpgrade}
            disabled={!selectedPlan || loading}
          />
        </div>
      </div>
    </div>
  );
};

export default Subscription;
